import React from 'react';
import { useParams, useNavigate, useLocation } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { 
  faUser,
  faSignOutAlt,
  faChartLine,
  faClipboardList,
  faBoxes,
  faHome
} from '@fortawesome/free-solid-svg-icons';
import wedlLogo from '../../assets/images/wedl_logo.svg';
import CompactSupplierSwitcher from './CompactSupplierSwitcher';

interface SupplierNavbarProps {
  supplierName: string;
  masterToken?: string;
  pendingQuestionnaires?: number;
  totalArticles?: number;
}

const SupplierNavbar: React.FC<SupplierNavbarProps> = ({
  supplierName,
  masterToken,
  pendingQuestionnaires = 0,
  totalArticles = 0
}) => {
  const { accessToken } = useParams<{ accessToken: string }>();
  const navigate = useNavigate();
  const location = useLocation();
  
  // Use masterToken prop if available, otherwise fall back to accessToken from URL
  const tokenForRedirect = masterToken || accessToken;

  // Determine current page for active navigation
  const currentPath = location.pathname;
  const isActivePage = (path: string) => currentPath.includes(path);

  const handleNavigation = (page: string) => {
    if (tokenForRedirect) {
      console.log(`🧭 Navigating to: /supplier/portal/${tokenForRedirect}/${page}`);
      navigate(`/supplier/portal/${tokenForRedirect}/${page}`);
    } else {
      console.log('❌ No token for redirect available');
    }
  };

  const handleSupplierSwitch = (supplierId: string, supplierName: string) => {
    console.log('🔄 Supplier switch initiated:', { supplierId, supplierName });
    
    // **SESSION STORAGE UPDATE**
    const currentSupplierInfo = sessionStorage.getItem('supplierInfo');
    let isBackToOriginal = false;
    
    if (currentSupplierInfo) {
      try {
        const supplierData = JSON.parse(currentSupplierInfo);
        isBackToOriginal = supplierData._id === supplierId;
        console.log('🔍 Checking if switching back to original portal owner:', isBackToOriginal);
      } catch (e) {
        console.log('⚠️ Could not parse supplier info');
      }
    }
    
    if (isBackToOriginal) {
      console.log('🏠 Switching back to original portal owner - clearing context');
      sessionStorage.removeItem('activeSupplierContext');
      
      // **SET MASTER SUPPLIER INFO TO SESSION**
      if (currentSupplierInfo) {
        const masterInfo = JSON.parse(currentSupplierInfo);
        sessionStorage.setItem('supplierInfo', JSON.stringify({
          ...masterInfo,
          supplierType: 'master'
        }));
      }
    } else {
      console.log('🔄 Switching to linked supplier - setting context');
      sessionStorage.setItem('activeSupplierContext', JSON.stringify({
        supplierId: supplierId,
        name: supplierName,
        supplierType: 'linked',
        switchedAt: new Date().toISOString()
      }));
    }
    
    // **DISPATCH EVENT TO UPDATE COMPONENTS IMMEDIATELY**
    window.dispatchEvent(new CustomEvent('supplierSwitched', { 
      detail: { 
        supplierId, 
        name: supplierName, 
        isBackToOriginal,
        supplierType: isBackToOriginal ? 'master' : 'linked'
      } 
    }));
    
    // **TRIGGER PAGE RELOAD TO REFRESH DATA**
    console.log('🔄 Reloading page to refresh supplier context');
    window.location.reload();
  };

  const handleLogout = () => {
    console.log('🔓 Supplier logout initiated');
    
    // **CLEAR SESSION DATA - ALLE STORAGE ARTEN**
    sessionStorage.removeItem('supplierSessionToken');
    sessionStorage.removeItem('supplierSessionExpiry');
    sessionStorage.removeItem('fromSupplierLogin');
    sessionStorage.removeItem('supplierPortalSession');
    sessionStorage.removeItem('supplierPortalSessionTime');
    sessionStorage.removeItem('supplierInfo');
    
    // **AUCH localStorage CLEAREN (falls noch alte Daten da sind)**
    localStorage.removeItem('supplierSessionToken');
    localStorage.removeItem('supplierSessionExpiry');
    
    // **🔐 REDIRECT ZUR SUPPLIER PORTAL LOGIN-SEITE**
    if (tokenForRedirect) {
      window.location.href = `/supplier/portal/${tokenForRedirect}/login`;
    } else {
      // Fallback zur allgemeinen Portal-Seite
      window.location.href = '/supplier/portal/';
    }
  };

  return (
    <nav className="wedl-navbar bg-dark navbar-dark sticky top-0 z-40 border-b border-gray-200 overflow-visible">
      <div className="navbar-container max-w-full mx-auto px-4 h-16 flex items-center justify-between overflow-visible">
        
        {/* **BRAND/LOGO** */}
        <div className="wedl-navbar-brand flex items-center">
          <img 
            src={wedlLogo} 
            alt="WEDL Logo" 
            className="wedl-navbar-logo h-8 w-auto"
          />
          <div className="ml-3">
            <div className="wedl-brand-text text-white font-semibold">
              Lieferantenportal
            </div>
            <div className="text-xs text-gray-300">
              Fragebogen und Artikel-System
            </div>
          </div>
        </div>

        {/* **NAVIGATION MENU** */}
        <div className="flex items-center space-x-1">
          <button
            onClick={() => handleNavigation('dashboard')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              isActivePage('dashboard') 
                ? 'bg-white/20 text-white' 
                : 'text-gray-300 hover:text-white hover:bg-white/10'
            }`}
          >
            <FontAwesomeIcon icon={faChartLine} />
            Dashboard
          </button>
          
          <button
            onClick={() => handleNavigation('questionnaires')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              isActivePage('questionnaires') 
                ? 'bg-white/20 text-white' 
                : 'text-gray-300 hover:text-white hover:bg-white/10'
            }`}
          >
            <FontAwesomeIcon icon={faClipboardList} />
            Fragebögen
          </button>
          
          <button
            onClick={() => handleNavigation('articles')}
            className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors flex items-center gap-2 ${
              isActivePage('articles') 
                ? 'bg-white/20 text-white' 
                : 'text-gray-300 hover:text-white hover:bg-white/10'
            }`}
          >
            <FontAwesomeIcon icon={faBoxes} />
            Meine Artikel
          </button>
        </div>

        {/* **RIGHT SIDE - SUPPLIER SWITCHER & LOGOUT** */}
        <div className="flex items-center space-x-4">
          
          {/* **INTEGRATED SUPPLIER SWITCHER (replaces user info)** */}
          {masterToken && (
            <CompactSupplierSwitcher 
              currentMasterToken={masterToken}
              currentSupplierName={supplierName}
              onSwitchSupplier={handleSupplierSwitch}
            />
          )}
          
          {/* **FALLBACK USER INFO (wenn kein masterToken)** */}
          {!masterToken && (
            <div className="flex items-center space-x-3">
              <div className="text-right">
                <div className="text-white text-sm font-medium">{supplierName}</div>
                <div className="text-gray-300 text-xs">Lieferant</div>
              </div>
              <div className="wedl-user-avatar w-8 h-8 bg-gray-600 rounded-full flex items-center justify-center">
                <FontAwesomeIcon icon={faUser} className="text-white text-sm" />
              </div>
            </div>
          )}

          {/* **LOGOUT BUTTON** */}
          <button
            onClick={handleLogout}
            className="wedl-logout-btn flex items-center px-3 py-2 border border-white/30 text-white text-sm rounded-md hover:bg-white/10 transition-colors"
          >
            <FontAwesomeIcon icon={faSignOutAlt} className="mr-2" />
            Abmelden
          </button>
        </div>
      </div>
    </nav>
  );
};

export default SupplierNavbar;